import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Checkbox,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Chip,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  Search,
  Sort,
  ArrowUpward,
  ArrowDownward,
  Refresh,
  CheckCircle,
  Close,
  Delete,
  ExpandMore,
  ExpandLess,
  Schedule,
  RestoreFromTrash,
  Visibility,
} from '@mui/icons-material';
import { useTheme, Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { Alert, AlertSort } from '../types/alerts';
import {
  formatTime,
  calculateDuration
} from '../utils/alertUtils';
import AlertActionDialog, { AlertActionType } from './AlertActionDialog';
import { StatusChip } from '../../../../components/shared';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { getCardBorder } from '../DashboardV2/types';

interface AlertsTableProps {
  alerts: Alert[];
  suppressedAlerts?: Alert[];
  loading?: boolean;
  isConnected?: boolean;
  onAcknowledge: (id: number, suppressMinutes?: number) => Promise<void>;
  onDismiss: (id: number, suppressMinutes?: number) => Promise<void>;
  onDelete: (id: number, suppressMinutes?: number) => Promise<void>;
  onRestore?: (id: number) => Promise<void>;
  onMarkReviewed?: (id: number) => Promise<void>;
  onFetchSuppressed?: () => Promise<void>;
  onRefresh?: () => void;
  sx?: SxProps<Theme>;
}

interface AlertTableRowProps {
  alert: Alert;
  isSuppressedView?: boolean;
  onRequestAction: (alertId: number, alertName: string, action: AlertActionType) => void;
  onRestore?: (id: number) => void;
}

/**
 * Check if a suppress_until timestamp is still active
 */
const isSuppressActive = (suppressUntil?: string): boolean => {
  if (!suppressUntil) return false;
  return new Date(suppressUntil) > new Date();
};

const LoadingRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {[...Array(count)].map((_, index) => (
      <TableRow key={index}>
        <TableCell colSpan={7}>
          <Skeleton variant="rounded" animation="wave" height={40} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

const NoAlerts: React.FC = () => (
  <TableRow>
    <TableCell colSpan={7}>
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Alerts Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All systems are running normally. No alerts to display.
        </Typography>
      </Box>
    </TableCell>
  </TableRow>
);

const AlertTableRow: React.FC<AlertTableRowProps> = ({
  alert,
  isSuppressedView = false,
  onRequestAction,
  onRestore
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const getSeverityChip = (severity: Alert['severity']) => {
    const colors = {
      critical: 'error' as const,
      warning: 'warning' as const,
      error: 'error' as const,
      info: 'info' as const
    };

    return (
      <Chip
        label={severity.toUpperCase()}
        color={colors[severity]}
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  const handleAcknowledge = () => onRequestAction(alert.id, alert.alertname, 'acknowledge');
  const handleDismiss = () => onRequestAction(alert.id, alert.alertname, 'dismiss');
  const handleDelete = () => onRequestAction(alert.id, alert.alertname, 'delete');
  const handleReview = () => onRequestAction(alert.id, alert.alertname, 'review');

  const suppressActive = isSuppressActive(alert.suppress_until);

  return (
    <>
      <TableRow
        sx={{
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
          },
          opacity: (alert.status === 'resolved' && !isSuppressedView) || isSuppressedView ? 0.7 : 1
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'collapse' : 'expand'}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {alert.alertname}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {alert.id}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>{getSeverityChip(alert.severity)}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusChip status={alert.status} type="alert" />
            {suppressActive && (
              <Tooltip title={`Suppressed until ${formatTime(alert.suppress_until!)}`}>
                <Schedule fontSize="small" color="action" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ maxWidth: 300 }}>
            {alert.summary}
          </Typography>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2">
              {formatTime(alert.starts_at)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Duration: {calculateDuration(alert.starts_at, alert.ends_at)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          {isSuppressedView ? (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {onRestore && (
                <Tooltip title="Restore Alert">
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<RestoreFromTrash />}
                    onClick={() => onRestore(alert.id)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Restore
                  </Button>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {alert.status === 'auto_resolved' && (
                <Tooltip title="Mark as Reviewed">
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    startIcon={<Visibility />}
                    onClick={handleReview}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Review
                  </Button>
                </Tooltip>
              )}

              {alert.status === 'new' && (
                <Tooltip title="Acknowledge Alert">
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<CheckCircle />}
                    onClick={handleAcknowledge}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Ack
                  </Button>
                </Tooltip>
              )}

              {(alert.status === 'new' || alert.status === 'acknowledged') && (
                <Tooltip title="Dismiss Alert">
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<Close />}
                    onClick={handleDismiss}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    Dismiss
                  </Button>
                </Tooltip>
              )}

              <Tooltip title="Delete Alert">
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDelete}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  Del
                </Button>
              </Tooltip>

            </Box>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded row with additional details */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Alert Details
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Description:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {alert.description}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Created:</strong> {formatTime(alert.created_at)}
                  </Typography>
                  {alert.updated_at && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Updated:</strong> {formatTime(alert.updated_at)}
                    </Typography>
                  )}
                </Box>
                {alert.ends_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Ends:</strong> {formatTime(alert.ends_at)}
                    </Typography>
                  </Box>
                )}
                {alert.status_changed_by && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{alert.status === 'acknowledged' ? 'Acknowledged' : 'Changed'} by:</strong> {alert.status_changed_by}
                      {alert.status_changed_at && ` at ${formatTime(alert.status_changed_at)}`}
                    </Typography>
                  </Box>
                )}
                {suppressActive && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Suppressed until:</strong> {formatTime(alert.suppress_until!)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  suppressedAlerts = [],
  loading = false,
  isConnected = false,
  onAcknowledge,
  onDismiss,
  onDelete,
  onRestore,
  onMarkReviewed,
  onFetchSuppressed,
  onRefresh,
  sx = {}
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  // State hooks
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['new', 'acknowledged', 'auto_resolved']);
  const [sortBy, setSortBy] = useState<AlertSort>('updated_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: AlertActionType;
    alertId: number;
    alertName: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const showingSuppressed = selectedStatuses.includes('suppressed');

  const handleConfirmAction = async (suppressMinutes?: number) => {
    if (!actionDialog) return;
    setActionLoading(true);
    try {
      switch (actionDialog.action) {
        case 'acknowledge':
          await onAcknowledge(actionDialog.alertId, suppressMinutes);
          break;
        case 'dismiss':
          await onDismiss(actionDialog.alertId, suppressMinutes);
          break;
        case 'delete':
          await onDelete(actionDialog.alertId, suppressMinutes);
          break;
        case 'review':
          if (onMarkReviewed) await onMarkReviewed(actionDialog.alertId);
          break;
      }
    } finally {
      setActionLoading(false);
      setActionDialog(null);
    }
  };

  const handleRequestAction = (alertId: number, alertName: string, action: AlertActionType) => {
    setActionDialog({ open: true, action, alertId, alertName });
  };

  const handleStatusFilterChange = (newStatuses: string[]) => {
    setSelectedStatuses(newStatuses);
    // Fetch suppressed alerts when the filter is first activated
    if (newStatuses.includes('suppressed') && !selectedStatuses.includes('suppressed') && onFetchSuppressed) {
      onFetchSuppressed();
    }
  };

  const filteredAndSortedAlerts = useMemo(() => {
    // When suppressed filter is active, show suppressed alerts instead
    const sourceAlerts = showingSuppressed ? suppressedAlerts : (alerts || []);
    const safeAlerts = sourceAlerts || [];

    let filtered = safeAlerts.filter(alert => {
      const matchesFilter = showingSuppressed || selectedStatuses.length === 0 || selectedStatuses.includes(alert.status);
      const matchesSearch = searchTerm === '' ||
        alert.alertname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.severity.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (alert.labels && JSON.stringify(alert.labels).toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesFilter && matchesSearch;
    });

    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'severity':
          const severityOrder = { critical: 4, error: 3, warning: 2, info: 1 };
          comparison = (severityOrder[a.severity] || 0) - (severityOrder[b.severity] || 0);
          break;
        case 'alertname':
          comparison = a.alertname.localeCompare(b.alertname);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'updated_at':
        default:
          const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          comparison = aTime - bTime;
          break;
      }

      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [alerts, suppressedAlerts, showingSuppressed, selectedStatuses, sortBy, sortDirection, searchTerm]);

  const handleSort = (newSortBy: AlertSort) => {
    if (sortBy === newSortBy) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortDirection('desc');
    }
  };

  const getStatusCounts = useMemo(() => {
    const safeAlerts = alerts || [];
    return {
      new: safeAlerts.filter(a => a.status === 'new').length,
      acknowledged: safeAlerts.filter(a => a.status === 'acknowledged').length,
      auto_resolved: safeAlerts.filter(a => a.status === 'auto_resolved').length,
      resolved: safeAlerts.filter(a => a.status === 'resolved').length,
      suppressed: suppressedAlerts.length,
    };
  }, [alerts, suppressedAlerts]);

  return (
    <Paper
      elevation={0}
      sx={{
        border: getCardBorder(theme),
        borderRadius: 2,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header with search and filters */}
      <Box sx={{
        p: 2,
        borderBottom: getCardBorder(theme),
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}>
          <Typography variant="h6" fontWeight="bold">
            {showingSuppressed ? 'Suppressed Alerts' : 'Alerts'} ({filteredAndSortedAlerts.length})
          </Typography>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', sm: 'center' }
          }}>
            <TextField
              size="small"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: 'auto', sm: 200 } }}
            />

            {/* Status Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                multiple
                value={selectedStatuses}
                onChange={(e) => handleStatusFilterChange(e.target.value as string[])}
                label="Filter by Status"
                renderValue={(selected) => {
                  if (selected.length === 0) return 'All';
                  if (selected.length === 1 && selected[0] === 'suppressed') return `Suppressed (${getStatusCounts.suppressed})`;
                  if (selected.length === 1) {
                    const status = selected[0];
                    const count = getStatusCounts[status as keyof typeof getStatusCounts];
                    return `${status.charAt(0).toUpperCase() + status.slice(1)} (${count})`;
                  }
                  return `${selected.length} selected`;
                }}
              >
                {[
                  { value: 'new', label: 'New', color: theme.palette.error.main },
                  { value: 'acknowledged', label: 'Acknowledged', color: theme.palette.warning.main },
                  { value: 'auto_resolved', label: 'Needs Review', color: theme.palette.info.main },
                  { value: 'resolved', label: 'Resolved', color: theme.palette.success.main },
                  { value: 'suppressed', label: 'Suppressed', color: theme.palette.text.disabled },
                ].map(({ value, label, color }) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox
                      checked={selectedStatuses.includes(value)}
                      size="small"
                      sx={{
                        color: color,
                        '&.Mui-checked': { color: color },
                        mr: 1
                      }}
                    />
                    <Typography variant="body2">
                      {label} ({getStatusCounts[value as keyof typeof getStatusCounts]})
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Conditional Refresh Button */}
            {!isConnected && onRefresh && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh sx={{
                  animation: loading ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} />}
                onClick={onRefresh}
                disabled={loading}
                sx={{ minWidth: 96 }}
              >
                Refresh
              </Button>
            )}

            {/* Sort Controls */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 'fit-content'
            }}>
              <Sort sx={{
                color: 'text.secondary',
                fontSize: 20,
              }} />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="sort-select-label">Sort by</InputLabel>
                <Select
                  labelId="sort-select-label"
                  value={sortBy}
                  label="Sort by"
                  onChange={(e) => setSortBy(e.target.value as AlertSort)}
                >
                  <MenuItem value="updated_at">Updated</MenuItem>
                  <MenuItem value="created_at">Created</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                  <MenuItem value="alertname">Name</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                onClick={() => handleSort(sortBy)}
                size="small"
                aria-label={`Sort ${sortDirection === 'desc' ? 'ascending' : 'descending'}`}
                sx={{
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'action.selected',
                  }
                }}
              >
                {sortDirection === 'desc' ? <ArrowDownward /> : <ArrowUpward />}
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile Card View / Desktop Table View */}
      {isMobile ? (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" animation="wave" height={100} />
              ))
            ) : filteredAndSortedAlerts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>No Alerts Found</Typography>
                <Typography variant="body2" color="text.secondary">All systems are running normally.</Typography>
              </Box>
            ) : (
              filteredAndSortedAlerts.map((alert) => (
                <Paper key={alert.id} elevation={0} sx={{ p: 2, border: getCardBorder(theme), borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={alert.severity.toUpperCase()} color={alert.severity === 'critical' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'} size="small" sx={{ fontWeight: 'bold' }} />
                      {isSuppressActive(alert.suppress_until) && (
                        <Tooltip title={`Suppressed until ${formatTime(alert.suppress_until!)}`}>
                          <Schedule fontSize="small" color="action" />
                        </Tooltip>
                      )}
                    </Box>
                    <StatusChip status={alert.status} type="alert" />
                  </Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>{alert.alertname}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{alert.summary}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{formatTime(alert.starts_at)} • {calculateDuration(alert.starts_at, alert.ends_at)}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {showingSuppressed ? (
                        onRestore && (
                          <Button size="small" variant="outlined" color="primary" startIcon={<RestoreFromTrash />} onClick={() => onRestore(alert.id)} sx={{ minWidth: 'auto', px: 1 }}>
                            Restore
                          </Button>
                        )
                      ) : (
                        <>
                          {alert.status === 'auto_resolved' && (
                            <IconButton size="small" color="info" aria-label="Mark as reviewed" onClick={() => handleRequestAction(alert.id, alert.alertname, 'review')}><Visibility fontSize="small" /></IconButton>
                          )}
                          {alert.status === 'new' && (
                            <IconButton size="small" color="warning" aria-label="Acknowledge alert" onClick={() => handleRequestAction(alert.id, alert.alertname, 'acknowledge')}><CheckCircle fontSize="small" /></IconButton>
                          )}
                          {(alert.status === 'new' || alert.status === 'acknowledged') && (
                            <IconButton size="small" color="success" aria-label="Dismiss alert" onClick={() => handleRequestAction(alert.id, alert.alertname, 'dismiss')}><Close fontSize="small" /></IconButton>
                          )}
                          <IconButton size="small" color="error" aria-label="Delete alert" onClick={() => handleRequestAction(alert.id, alert.alertname, 'delete')}><Delete fontSize="small" /></IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </Box>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }} />
                <TableCell sx={{ fontWeight: 'bold' }}>Alert Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Severity</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Summary</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <LoadingRows count={filteredAndSortedAlerts.length > 0 ? filteredAndSortedAlerts.length : 5} />
              ) : (
                <>
                  {filteredAndSortedAlerts.length === 0 && <NoAlerts />}
                  {filteredAndSortedAlerts.map((alert) => (
                    <AlertTableRow
                      key={alert.id}
                      alert={alert}
                      isSuppressedView={showingSuppressed}
                      onRequestAction={handleRequestAction}
                      onRestore={onRestore}
                    />
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {actionDialog && (
        <AlertActionDialog
          open={actionDialog.open}
          onClose={() => setActionDialog(null)}
          onConfirm={handleConfirmAction}
          action={actionDialog.action}
          alertName={actionDialog.alertName}
          loading={actionLoading}
        />
      )}
    </Paper>
  );
};
